import React from "react";
import { motion } from "framer-motion";
import { styled } from "styled-components";

const IngredientHeader = styled.h3`
  font-family: Lobster, sans-serif;
  color: #4d1312;
  margin-right: 0.5rem;
  line-height: 130%;
  text-shadow: 1px 1px 0 #4d1312, 1px 1px 0 #4d1312, 2px 2px 0 #fff;
  font-size: 3.2em;

  @media (max-width: 700px) {
    font-size: 2.2em;
    text-shadow: 1px 1px 0 #4d1312, 1px 1px 0 #4d1312, 1px 1px 0 #fff;
  }

  @media (min-width: 1550px) {
    text-shadow: 1px 1px 0 #4d1312, 2px 2px 0 #4d1313, 4px 4px 0 #fff;
  }
`;

// Left-aligned ingredients list component with capsules next to it
const IngredientsSection = () => {
  const ingredients = [
    { id: "tribulus", name: "Tribulus terrestris", amount: "500mg*" },
    { id: "ashwagandha", name: "Ashwagandha", amount: "150mg*" },
    { id: "tongkat", name: "Tongkat Ali", amount: "200mg*" },
    { id: "epimedium", name: "Epimedium", amount: "160mg*" },
    { id: "dim", name: "DIM", amount: "100mg*" },
    { id: "zinc", name: "Zinc", amount: "20mg*" },
  ];

  return (
    <div className="relative w-full mb-[-5px] overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-[#e9b420]" />
      <div
        className="absolute top-0 right-[33%] w-[30.1%] h-full bg-[#a41e24] hidden md:block"
        style={{
          clipPath: "polygon(100% 0, 0 0, 100% 100%)",
          transform: "translateX(1px)",
        }}
      />
      <div className="absolute top-0 right-0 w-[33%] h-full bg-[#a41e24] hidden md:block" />

      {/* Content Container */}
      <div className="relative z-10  mx-auto px-4 py-8 md:py-12">
        {/* Mobile Title - Only visible on mobile */}
        <div className="md:hidden text-center mb-8">
          <h2
            className="text-[3.2rem] md:text-6xl lg:text-8xl tracking-wider leading-tight [@media(min-width:1550px)]Container:text-[6.7rem] text-white"
            style={{
              fontFamily: "Headlined, sans-serif",
              textShadow: "1px 1px 0 #4d1312, 2px 2px 0 #4d1312",
            }}
          >
            PREMIUM
            <br />
            INGREDIENTS
          </h2>
        </div>

        <div className="flex flex-col md:flex-row">
          {/* Left section (yellow) with ingredients list and capsules */}
          <div className="w-full md:w-2/3 flex flex-row">
            {/* Ingredients List */}
            <div className="w-full min-w-80 md:min-w-96 lg:min-w-96 xl:w-1/2 pl-4 md:pl-8 xl:pl-12 mb-8 md:mb-0">
              <div className="space-y-4 md:space-y-6">
                {ingredients.map((ingredient) => (
                  <motion.div
                    key={ingredient.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: 0.1 * ingredients.indexOf(ingredient),
                    }}
                  >
                    <div className="flex items-baseline">
                      <IngredientHeader className="ingredients">
                        {ingredient.name}
                      </IngredientHeader>
                      <p
                        className="text-gray-800 pl-1 text-lg [@media(min-width:1550px)]:text-[1.5em]"
                        style={{
                          fontFamily: "Veneer, sans-serif",
                        }}
                      >
                        {ingredient.amount}
                      </p>
                    </div>
                  </motion.div>
                ))}
                <div
                  className="text-gray-800 pl-1 text-lg [@media(min-width:1550px)]:text-[1.5em]"
                  style={{
                    fontFamily: "Veneer, sans-serif",
                  }}
                >
                  * Per Serving
                </div>
              </div>
            </div>

            {/* Capsule image - next to ingredients in yellow section */}
            <div className="w-full min-w-[50vw] md:min-w-[30vw] lg:min-w-0 relative right-36 lg:right-0 xl:w-1/3 [@media(min-width:1550px)]:w-[40%] flex items-end justify-start">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src="/media/capsules2.png"
                  alt="Capsules"
                  className="object-contain"
                />
              </motion.div>
            </div>
          </div>

          {/* Desktop Title section - Hidden on mobile */}
          <div className="w-full md:w-1/3 mt-8 md:mt-0">
            <div className="flex flex-col items-center md:items-end justify-between h-full md:pr-8 lg:pr-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center md:text-right mb-8 hidden md:block"
              >
                <h2
                  className="text-4xl md:text-6xl lg:text-8xl [@media(min-width:1550px)]:text-[6.7rem] text-white"
                  style={{
                    fontFamily: "Headlined, sans-serif",
                    textShadow: "2px 2px 0 #4d1312, 3px 3px 0 #4d1312",
                  }}
                >
                  PREMIUM
                  <br />
                  INGREDIENTS
                </h2>
              </motion.div>

              {/* Desktop certification logos */}
              <div className="hidden md:flex md:flex-col items-center md:items-end">
                <div className="mb-6">
                  <img
                    src="/media/made-in-usa.svg"
                    alt="made in usa"
                    className="h-20 md:h-24 [@media(min-width:1550px)]:h-[12rem]"
                  />
                </div>

                <div className="flex justify-center gap-4">
                  <img
                    src="/media/gmp.svg"
                    alt="GMP"
                    className="h-12 md:h-16 [@media(min-width:1550px)]:h-[7rem]"
                  />
                  <img
                    src="/media/lab-tested.svg"
                    alt="Lab Tested"
                    className="h-12 md:h-16 [@media(min-width:1550px)]:h-[7rem]"
                  />
                  <img
                    src="/media/non-gmo.svg"
                    alt="Non GMO"
                    className="h-12 md:h-16 [@media(min-width:1550px)]:h-[7rem]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Red background certification section - MOBILE ONLY */}
        <div className="mt-8 bg-[#a41e24] py-6 -ml-5 w-[110%]  md:hidden">
          <div className="flex flex-col items-center">
            {/* USA Made Section */}
            <div className="mb-4 text-center">
              <div className="flex flex-col items-center justify-center">
                {/* <p className="text-white text-xl font-bold mb-2">MADE IN USA</p> */}
                <img
                  src="/media/made-in-usa.svg"
                  alt="made in usa"
                  className="h-40"
                />
              </div>
            </div>
            
            {/* Certification Icons */}
            <div className="flex justify-center gap-8 mt-6">
              <div className="flex flex-col items-center">
                <img
                  src="/media/gmp.svg"
                  alt="GMP"
                  className="h-16"
                />
              </div>
              <div className="flex flex-col items-center">
                <img
                  src="/media/lab-tested.svg"
                  alt="Lab Tested"
                  className="h-16"
                />
              </div>
              <div className="flex flex-col items-center">
                <img
                  src="/media/non-gmo.svg"
                  alt="Non GMO"
                  className="h-16"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IngredientsSection;